import { useState, useEffect, useCallback } from 'react';

// Generic data-fetching hook that captures the load/data/error/loading pattern
// repeated across CIRA dashboard components.
//
// Usage:
//   const { data, loading, error, refetch } = useApiData(() => getAllCompanies(), []);
//
// - fetchFn: an async function returning the data (typically an apiservice call).
// - deps:    dependency array controlling when to refetch (same semantics as useEffect deps).
//
// The fetch runs on mount and whenever `deps` change. `refetch()` re-runs it on demand.
export function useApiData(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run().catch(() => {
      // error already captured in state; swallow to avoid unhandled rejection
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: run };
}
